import type { UseQueryResult } from "./index";
import type { MutableRefObject } from "react";

type QueryResultStatus = "idle" | "loading" | "success" | "error";

interface QueryResultEntry {
  readonly data: unknown;
  readonly error: unknown;
  readonly status: QueryResultStatus;
}

interface QueryResultInput<TData> {
  readonly entry: QueryResultEntry | undefined;
  readonly displayData: TData | undefined;
  readonly refetch: () => Promise<void>;
  readonly isRefetching: boolean;
  readonly enabled: boolean;
}

export function buildQueryResult<TData>(
  input: QueryResultInput<TData>,
): UseQueryResult<TData> {
  return {
    data: input.displayData,
    // TanStack v5 parity (admin cache audit): placeholderData (e.g. the
    // keepPreviousData sentinel) means there IS something to render, so the
    // query is not "loading" — paginated tables keep the previous rows on
    // page/filter changes instead of flashing a skeleton/empty state.
    isLoading:
      input.enabled &&
      input.displayData === undefined &&
      (!input.entry ||
        ((input.entry.status === "loading" || input.entry.status === "idle") &&
          !input.entry.data)),
    isSuccess: input.entry?.status === "success",
    isError: input.entry?.status === "error",
    error: (input.entry?.error ?? null) as Error | null,
    refetch: input.refetch,
    isRefetching: input.isRefetching,
    // Display data without settled cache data for this key ⇒ the UI is
    // looking at placeholder (kpd) bytes. Consumers that append/merge pages
    // must skip these frames (see TokensTab cursor pagination).
    isPlaceholderData:
      input.displayData !== undefined && input.entry?.data === undefined,
  };
}

type PlaceholderData<TData> =
  | ((prev: TData | undefined) => TData | undefined)
  | TData;

export function resolvePlaceholder<TData>(
  rawData: TData | undefined,
  placeholderData: PlaceholderData<TData> | undefined,
  prevRef: MutableRefObject<TData | undefined>,
): TData | undefined {
  if (rawData !== undefined) {
    prevRef.current = rawData;
    return rawData;
  }
  if (placeholderData === undefined) return undefined;
  return typeof placeholderData === "function"
    ? (placeholderData as (prev: TData | undefined) => TData | undefined)(
        prevRef.current,
      )
    : placeholderData;
}
